import React from "react";

import { theme } from "@tm/utility/global";
import FormattedText, {
  getPlainText,
  type FormattedTextProps,
} from "../../../../components/FormattedText/FormattedText";
import Spinner from "../../../../components/Spinner/Spinner";
import { ChevronUpIcon } from "../../../../icons";
import { useClaimBenefitDetails } from "../../hooks/useClaimBenefitDetails";
import type {
  ClaimCategoryDetailsData,
  ClaimInfoViewProps,
  ClaimStaticPageData,
  HelpContactCardData,
  StaticPageBodyComponent,
  TextNode,
} from "../../types";
import {
  DetailsCard,
  ErrorContainer,
  ErrorText,
  HelpCard,
  HelpContactText,
  HelpTitle,
  InfoContainer,
  InfoGrid,
  InfoLeftColumn,
  InfoPage,
  InfoRightColumn,
  LoadingContainer,
  PageHeader,
  PageIcon,
  PageSubtitle,
  PageTitle,
  PageTitleGroup,
  PreviousButton,
  PreviousIconWrapper,
  RetryButton,
  SectionContent,
  SectionItemBlock,
  SectionItemDesc,
  SectionItemName,
  SectionItemsGroup,
  SectionNumber,
  SectionsContainer,
  SectionSeparator,
  SectionTitle,
  SectionWrapper,
} from "./ClaimInfoView.style";

const HTML_TAG_REGEX = /<\/?[a-z][\s\S]*?>/i;

function resolveTextValue(
  node: TextNode | undefined
): FormattedTextProps["value"] {
  const raw = getPlainText(node);
  if (!raw) return undefined;
  if (HTML_TAG_REGEX.test(raw)) return { html: raw };
  return node as FormattedTextProps["value"];
}

const CARD_CLAIM_DETAILS = "ClaimCategoryDetailsCard";
const CARD_HELP_CONTACT = "HelpContactCard";

function findComponent(
  components: StaticPageBodyComponent[] | undefined,
  cardName: string
): Record<string, unknown> | undefined {
  return components?.find((c) => c?.cardName === cardName)?.data;
}

function ClaimCategoryDetailsSection({
  data,
}: {
  data: ClaimCategoryDetailsData;
}) {
  const sections = data?.sections ?? [];

  if (sections.length === 0) return null;

  return (
    <SectionsContainer>
      {sections.map((section, idx) => {
        const sectionNum =
          typeof section.id === "number" ? section.id : idx + 1;
        const items = section.items ?? [];

        return (
          <React.Fragment key={`section-${idx}`}>
            {idx > 0 ? <SectionSeparator /> : null}
            <SectionWrapper>
              <SectionNumber>{sectionNum}.</SectionNumber>
              <SectionContent>
                {section.title ? (
                  <SectionTitle>
                    <FormattedText value={section.title} />
                  </SectionTitle>
                ) : null}
                {items.length > 0 && (
                  <SectionItemsGroup>
                    {items.map((item, i) => {
                      const nameVal = resolveTextValue(item?.name);
                      const descVal = resolveTextValue(item?.description);
                      return (
                        <SectionItemBlock key={i}>
                          {nameVal ? (
                            <SectionItemName>
                              <FormattedText value={nameVal} />
                            </SectionItemName>
                          ) : null}
                          {descVal ? (
                            <SectionItemDesc>
                              <FormattedText value={descVal} />
                            </SectionItemDesc>
                          ) : null}
                        </SectionItemBlock>
                      );
                    })}
                  </SectionItemsGroup>
                )}
              </SectionContent>
            </SectionWrapper>
          </React.Fragment>
        );
      })}
    </SectionsContainer>
  );
}

function HelpContactSection({ data }: { data: HelpContactCardData }) {
  const contactHtml = data?.contactInfo?.text;

  return (
    <HelpCard $bgColor={data?.backgroundColor}>
      {data?.title ? (
        <HelpTitle>
          <FormattedText value={data.title} />
        </HelpTitle>
      ) : null}
      {contactHtml ? (
        <HelpContactText>
          <FormattedText value={{ html: contactHtml }} />
        </HelpContactText>
      ) : data?.description ? (
        <HelpContactText>
          <FormattedText value={data.description} />
        </HelpContactText>
      ) : null}
    </HelpCard>
  );
}

export function ClaimInfoView({
  params,
  bookingId,
  partnerBookingId,
  currency,
  onBack,
}: ClaimInfoViewProps) {
  const { fetchState, data, errorMessage, retry } = useClaimBenefitDetails({
    bookingId,
    partnerBookingId,
    currency,
    detailFetchParams: params?.detailFetchParams,
    contentType: params?.contentType,
  });

  const pageData = (data?.staticPageData ?? null) as ClaimStaticPageData | null;
  const hasPageData = Boolean(pageData);

  const detailsData = findComponent(
    pageData?.bodyComponents,
    CARD_CLAIM_DETAILS
  ) as ClaimCategoryDetailsData | undefined;

  const helpData = findComponent(
    pageData?.bodyComponents,
    CARD_HELP_CONTACT
  ) as HelpContactCardData | undefined;

  const pageTitle = detailsData?.title
    ? getPlainText(detailsData.title)
    : params?.categoryTitle?.trim() ?? "";

  const pageIcon = detailsData?.iconUrl ?? params?.categoryIcon;
  const previousCta = pageData?.previousCta?.text;

  if (fetchState === "loading") {
    return (
      <InfoPage>
        <InfoContainer>
          <LoadingContainer>
            <Spinner
              spinnerWidth={32}
              spinnerColor={theme.blueText}
              thickness={3}
            />
          </LoadingContainer>
        </InfoContainer>
      </InfoPage>
    );
  }

  if (fetchState === "error" || (fetchState === "success" && !hasPageData)) {
    const isFetchError = fetchState === "error";
    const message = isFetchError
      ? errorMessage
      : "No data available for this claim category.";
    return (
      <InfoPage>
        <InfoContainer>
          <ErrorContainer>
            <ErrorText>{message}</ErrorText>
            {isFetchError ? (
              <RetryButton onClick={retry} type="button">
                Try Again
              </RetryButton>
            ) : null}
            <PreviousButton onClick={onBack} type="button">
              <PreviousIconWrapper aria-hidden="true">
                <ChevronUpIcon color={theme.blueText} cursor="pointer" />
              </PreviousIconWrapper>
              {previousCta ? <FormattedText value={previousCta} /> : null}
            </PreviousButton>
          </ErrorContainer>
        </InfoContainer>
      </InfoPage>
    );
  }

  return (
    <InfoPage>
      <InfoContainer>
        <PageHeader>
          {pageIcon ? <PageIcon src={pageIcon} alt="" /> : null}
          <PageTitleGroup>
            {pageTitle ? <PageTitle>{pageTitle}</PageTitle> : null}
            {params?.categorySubtitle ? (
              <PageSubtitle>{params.categorySubtitle}</PageSubtitle>
            ) : null}
          </PageTitleGroup>
        </PageHeader>

        <InfoGrid>
          <InfoLeftColumn>
            <DetailsCard>
              {detailsData ? (
                <ClaimCategoryDetailsSection data={detailsData} />
              ) : null}
            </DetailsCard>

            <PreviousButton onClick={onBack} type="button">
              <PreviousIconWrapper aria-hidden="true">
                <ChevronUpIcon color={theme.blueText} cursor="pointer" />
              </PreviousIconWrapper>
              {previousCta ? <FormattedText value={previousCta} /> : null}
            </PreviousButton>
          </InfoLeftColumn>

          <InfoRightColumn>
            {helpData ? <HelpContactSection data={helpData} /> : null}
          </InfoRightColumn>
        </InfoGrid>
      </InfoContainer>
    </InfoPage>
  );
}
